/**
 * Price-intelligence derivation — the honest, testable view-model behind the
 * PDP "Fridge.com Price Intelligence" module (conversion/trust lane).
 *
 * Pure and client-safe: no supabase, no I/O. Every rendered number is derived
 * from OUR stored keepa_price_snapshots stats (passed in as `stats`, computed
 * upstream by components/deal-verdict/compute.ts) and the LIVE displayed offer
 * price (`currentCents`). It never touches products.lowest_price_ever_* (those
 * columns are populated for <120 rows and their provenance is unaudited).
 *
 * THE HONESTY CONTRACT (encoded as behaviour below and pinned by tests).
 * Re-cut 08/27/2026 on the owner's ruling (FRIDGE-583): "i want my price
 * charts on all products … needs to always be there no matter whatever
 * happens ever." The MODULE is now always present on any product with a live
 * price; what varies with the evidence is what it may SAY. What was never
 * weakened: no fabricated point, no interpolated gap, no comparative claim
 * below the 14-day floor, no line through fewer than MIN_SPARK_POINTS
 * observed readings.
 *   1. Returns null ONLY when currentCents is missing/<=0 — a See-Deal /
 *      price-unknown product has no live price, so it gets NO module. Every
 *      live PDP has a price, so on the live surface this is always-on.
 *   2. stats == null → the 'tracking-started' rung: the module renders the
 *      live price and says verified history is unavailable, claims nothing, charts
 *      nothing. (This used to hide the module on ~4,400 live pages.)
 *   3. stats present but the verdict gate fails (< 14 tracked sample days) →
 *      NO comparative claim (tier/headline/detail null, average withheld),
 *      but the REAL facts render: lowest tracked, tracked-since, reading
 *      count — and the ladder still decides the line on its own evidence.
 *      (This used to hide the module on ~1,350 pages that hold real
 *      readings.)
 *   4. The verdict, when it exists, is recomputed against the LIVE price so
 *      the "Current" number and the verdict math agree.
 *   5. The sparkline is emitted on the ladder's 'chart' rung (>=
 *      MIN_SPARK_POINTS real points that move) and, since FRIDGE-1151, on its
 *      'no-movement' rung (the same count, every reading one price), which
 *      draws flat. Below the count there is no line.
 */

import {
  computeVerdict,
  formatMonthYear,
  type PriceHistoryStats,
  type DealVerdictTier,
} from '@/components/deal-verdict/compute'
import { classifyPriceHistory, type PriceHistoryRung } from './history-ladder'

/** One real snapshot point: ISO date + integer cents. */
export interface SparkPoint {
  d: string
  c: number
}

export interface PriceIntelligenceVM {
  currentCents: number
  /** null below the 14-day claim floor — the module renders, the claim does not. */
  tier: DealVerdictTier | null
  /** true for the reassuring tiers (all-time-low / below-average). */
  positive: boolean
  headline: string | null
  detail: string | null
  /** null on the 'tracking-started' rung (no tracked history yet). */
  lowestCents: number | null
  lowestMonthYear: string | null
  averageCents: number | null
  averageLabel: string
  trackedSinceMonthYear: string | null
  sampleDays: number
  /** Real points to plot, or null when too few to be honest. */
  sparkline: SparkPoint[] | null
  /**
   * Which rung of the honest ladder this product earned (FRIDGE-282). The
   * renderer switches on this for its words; `sparkline` is non-null on the
   * 'chart' and 'no-movement' rungs (FRIDGE-1151) and null on every other.
   */
  rung: PriceHistoryRung | { kind: 'tracking-started' }
}

/** Minimum real snapshot points required before we draw a sparkline. */
export const MIN_SPARK_POINTS = 8

/** Maximum points we render (downsample above this upstream). */
export const MAX_SPARK_POINTS = 90

function cleanSpark(series: SparkPoint[] | null | undefined): SparkPoint[] {
  if (!Array.isArray(series)) return []
  return series.filter(
    (p): p is SparkPoint =>
      !!p &&
      typeof p.c === 'number' &&
      p.c > 0 &&
      typeof p.d === 'string' &&
      p.d.length >= 10
  )
}

/**
 * Build the module view-model, or null when the honesty gates say "hide".
 * `asOf` is forwarded to computeVerdict so tests can pin the 180-day-span
 * clock used by the all-time-low tier.
 */
export function derivePriceIntelligence(input: {
  currentCents: number | null | undefined
  stats: PriceHistoryStats | null | undefined
  series?: SparkPoint[] | null
  asOf?: Date
}): PriceIntelligenceVM | null {
  const { currentCents, stats, series, asOf } = input

  // Gate 1 — no live displayed price (See-Deal / unavailable) → no module.
  // The ONLY null path (owner ruling FRIDGE-583, 08/27/2026).
  if (typeof currentCents !== 'number' || !(currentCents > 0)) return null

  // Rung 0 — no admissible history: the module still renders the current offer.
  // Its legacy internal rung name does not prove capture is active or that the
  // current offer has been recorded in the historical ledger (FRIDGE-1003).
  if (!stats) {
    return {
      currentCents,
      tier: null,
      positive: false,
      headline: null,
      detail: null,
      lowestCents: null,
      lowestMonthYear: null,
      averageCents: null,
      averageLabel: 'Tracked avg',
      trackedSinceMonthYear: null,
      sampleDays: 0,
      sparkline: null,
      rung: { kind: 'tracking-started' },
    }
  }

  // Gate 2 (cont.) — verdict recomputed against the LIVE price. Null when the
  // < 14-sample-day floor is not met, so the module inherits that gate exactly.
  //
  // THIS GATE OWNS ONE QUESTION AND NOT THE OTHER (FRIDGE-375). It decides
  // whether a COMPARATIVE CLAIM may be made — "all-time low", "$X below the
  // 90-day average" — which is a claim about money on a public page. It does
  // NOT decide whether a line may be drawn; classifyPriceHistory below owns
  // that, and still refuses among the products that clear this floor.
  // Deliberately in this order: with no claim there is no module to draw into.
  // Measured 08/22/2026 — 119 of 1,471 products holding tracked history clear
  // this gate; the ladder then denies a flat line to one of them.
  const verdict = asOf
    ? computeVerdict(currentCents, stats, asOf)
    : computeVerdict(currentCents, stats)
  // Below the 14-day floor the CLAIM is withheld — tier/headline null and the
  // average cell withheld with it (an average of 5 days is a claim-shaped
  // number). The module itself still renders the real facts (owner ruling
  // FRIDGE-583); the ladder below still decides the line on its own evidence.

  const positive =
    !!verdict && (verdict.tier === 'all-time-low' || verdict.tier === 'below-average')

  const averageCents = verdict ? (stats.avg90Cents ?? stats.avg30Cents ?? null) : null
  const averageLabel = !verdict
    ? 'Tracked avg'
    : stats.avg90Cents != null
      ? '90-day tracked avg'
      : stats.avg30Cents != null
        ? '30-day tracked avg'
        : 'Tracked avg'

  // The honest ladder decides whether a line may be drawn at all. A price that
  // held across every reading keeps its own rung, so the card can SAY it held,
  // and since FRIDGE-1151 it is also drawn: a flat line through the observed
  // readings is a true statement about a price that did not move (the owner,
  // FRIDGE-1115: "everything needs a chart"). Thin and empty series still get
  // no line.
  const rung = classifyPriceHistory(series, MIN_SPARK_POINTS)
  const sparkline = rung.kind === 'chart' || rung.kind === 'no-movement' ? rung.points : null

  return {
    currentCents,
    tier: verdict ? verdict.tier : null,
    positive,
    headline: verdict ? verdict.headline : null,
    detail: verdict ? verdict.detail : null,
    lowestCents: stats.minCents,
    lowestMonthYear: formatMonthYear(stats.minDate),
    averageCents,
    averageLabel,
    trackedSinceMonthYear: formatMonthYear(stats.trackedSinceDate),
    sampleDays: stats.sampleDays,
    sparkline,
    rung,
  }
}

/**
 * FRIDGE-1253 — the stats the card is built from. The PDP payload carries the
 * tracked stats on their own (`priceStats`); `dealVerdict.stats` is only a
 * fallback, because computeVerdict returns null below the 14-day claim floor
 * and the card must still state the real facts there (rule 3 above). The claim
 * itself stays gated inside derivePriceIntelligence.
 */
export function priceIntelStatsFor(
  product: { priceStats?: PriceHistoryStats | null; dealVerdict?: { stats?: PriceHistoryStats | null } | null } | null | undefined,
): PriceHistoryStats | null {
  return product?.priceStats ?? product?.dealVerdict?.stats ?? null
}
